import type {
  EvolutionCell,
  EvolutionMatrix,
  EvolutionRow,
  PeriodRunsRow,
  WorkflowStats,
} from "@/lib/analytics/types";

/**
 * Compute a delta cell value between two consecutive bucket counts.
 * Encodes the 4 possible states: null (first), new, gone, ratio.
 */
function computeDelta(
  prev: number,
  current: number,
): EvolutionCell["delta"] {
  if (prev === 0 && current === 0) return null; // zero-zero: treat as no-delta (first bucket behaviour)
  if (prev === 0 && current > 0) return { kind: "new" };
  if (prev > 0 && current === 0) return { kind: "gone" };
  return { kind: "ratio", value: (current - prev) / prev };
}

/**
 * Build a single EvolutionRow from a per-period count array.
 * i=0 always gets delta=null (no predecessor).
 */
function buildRow(
  key: EvolutionRow["key"],
  name: string,
  counts: number[],
): EvolutionRow {
  const cells: EvolutionCell[] = counts.map((runs, i) => {
    if (i === 0) return { runs, delta: null };
    const prev = counts[i - 1] ?? 0;
    return { runs, delta: computeDelta(prev, runs) };
  });
  const total = counts.reduce((acc, c) => acc + c, 0);
  // Pre-computed once here so the UI doesn't need to map cells on every render.
  return { key, name, cells, total, sparklineData: counts };
}

/**
 * Compute the evolution matrix from pre-computed runsOverTime rows and
 * workflowStats (for naming + ranking).
 *
 * Returns `{ periods: [], rows: [] }` when either input is empty, which
 * triggers the EmptyState guard in dashboard.tsx.
 *
 * When `allPeriods` is provided, it takes precedence over the periods
 * implied by `rows` — empty periods (no runs in that bucket) become
 * zero-filled columns rather than disappearing from the table. Callers
 * typically derive it from `enumeratePeriods(windowStart, windowEnd, bucket)`
 * so the matrix always spans the full selected window.
 *
 * Complexity: O(P × W) where P = number of periods, W = number of workflows.
 * Upper bound: 400 × 12 = 4 800 cells — negligible.
 */
export function computeEvolutionMatrix(
  rows: PeriodRunsRow[],
  workflows: WorkflowStats[],
  limit = 10,
  allPeriods?: string[],
): EvolutionMatrix {
  if (workflows.length === 0) {
    return { periods: [], rows: [] };
  }
  if (rows.length === 0 && (!allPeriods || allPeriods.length === 0)) {
    return { periods: [], rows: [] };
  }

  // If an explicit period list is supplied we show every bucket in the
  // selected window — even ones with zero runs. Otherwise fall back to
  // the union of periods present in `rows` (legacy behaviour).
  const periods =
    allPeriods && allPeriods.length > 0 ? [...allPeriods] : rows.map((r) => r.period);
  const periodIndex = new Map<string, number>();
  for (let i = 0; i < periods.length; i++) {
    const p = periods[i];
    if (p !== undefined) periodIndex.set(p, i);
  }

  // Build a lookup: workflowId → index in `periods` → count.
  // Single pass over rows × workflows.
  const workflowIdSet = new Set(workflows.map((w) => w.workflowId));

  // Accumulate per-workflow counts across periods.
  const countsByWorkflow = new Map<number, number[]>();
  for (const wfId of workflowIdSet) {
    countsByWorkflow.set(wfId, new Array<number>(periods.length).fill(0));
  }

  for (const row of rows) {
    const pi = periodIndex.get(row.period);
    if (pi === undefined) continue; // Period outside the requested window — skip.
    for (const [wfIdStr, count] of Object.entries(row.perWorkflow)) {
      const wfId = Number(wfIdStr);
      const arr = countsByWorkflow.get(wfId);
      if (arr) arr[pi] = count;
    }
  }

  // Sort workflows by total runs desc, then alpha for stability.
  const sortedWorkflows = [...workflows].sort((a, b) => {
    const diff = b.runs - a.runs;
    return diff !== 0 ? diff : a.name.localeCompare(b.name);
  });

  const topWorkflows = sortedWorkflows.slice(0, limit);
  const otherWorkflows = sortedWorkflows.slice(limit);

  const resultRows: EvolutionRow[] = [];

  // Top N rows.
  for (const wf of topWorkflows) {
    const counts = countsByWorkflow.get(wf.workflowId) ?? new Array<number>(periods.length).fill(0);
    resultRows.push(buildRow(wf.workflowId, wf.name, counts));
  }

  // "Other" row — aggregate of workflows outside top N.
  if (otherWorkflows.length > 0) {
    const otherCounts = new Array<number>(periods.length).fill(0);
    for (const wf of otherWorkflows) {
      const counts = countsByWorkflow.get(wf.workflowId) ?? [];
      for (let pi = 0; pi < periods.length; pi++) {
        otherCounts[pi] = (otherCounts[pi] ?? 0) + (counts[pi] ?? 0);
      }
    }
    const otherRow = buildRow("other", "Other", otherCounts);
    // Only add if Other has any runs at all.
    if (otherRow.total > 0) {
      resultRows.push(otherRow);
    }
  }

  // "Total" row — aggregate of ALL workflows (top N + other).
  const totalCounts = new Array<number>(periods.length).fill(0);
  for (const wf of sortedWorkflows) {
    const counts = countsByWorkflow.get(wf.workflowId) ?? [];
    for (let pi = 0; pi < periods.length; pi++) {
      totalCounts[pi] = (totalCounts[pi] ?? 0) + (counts[pi] ?? 0);
    }
  }
  resultRows.push(buildRow("total", "Total", totalCounts));

  return { periods, rows: resultRows };
}
